import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#bf360c';
const PRIMARY_MID = '#e64a19';
const PRIMARY_LT  = '#fbe9e7';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtV = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const GrowthBadge = ({ cur, prev }) => {
    if (!prev) return null;
    const pct = ((cur - prev) / prev) * 100;
    const up = pct >= 0;
    return (
        <span style={{
            marginLeft: 4, fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
            background: up ? '#dcfce7' : '#fee2e2',
            color: up ? '#059669' : '#dc2626',
        }}>
            {up ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
        </span>
    );
};

// Build pivot: { itemId -> { ItemName, months: { monthNo -> { cur, prev, diff, growth } } } }
const buildPivot = (rows) => {
    const map = {};
    const order = [];
    const monthSet = new Set();
    rows.forEach(r => {
        const key = r.ItemId;
        if (!map[key]) { map[key] = { ItemId: key, ItemName: r.ItemName, months: {} }; order.push(key); }
        map[key].months[r.MonthNo] = {
            cur:    parseFloat(r.CurrentPeriodAmount)  || 0,
            prev:   parseFloat(r.PreviousPeriodAmount) || 0,
            diff:   parseFloat(r.DifferenceAmount)     || 0,
            growth: parseFloat(r.GrowthPercentage)     || 0,
        };
        monthSet.add(r.MonthNo);
    });
    const months = [...monthSet].sort((a, b) => a - b);
    return { items: order.map(k => map[k]), months };
};

const YearOnYearPurchaseReport = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), 3, 1))); // Apr 1
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [rawData, setRawData]   = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');
    const [view, setView]         = useState('both'); // 'current' | 'previous' | 'both'

    useEffect(() => {
        if (companyId && branchId && token) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'PurchaseAnalysisReportApi',
                `/YearToYearComparison/${fromDate}/${toDate}/${companyId}/${branchId}`,
                token
            );
            const rows = res?.response_data || res || [];
            setRawData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch year on year purchase comparison');
        } finally {
            setLoading(false);
        }
    };

    const { items, months } = useMemo(() => buildPivot(rawData), [rawData]);

    const filtered = useMemo(() => {
        if (!search.trim()) return items;
        const q = search.toLowerCase();
        return items.filter(r => (r.ItemName || '').toLowerCase().includes(q));
    }, [items, search]);

    const monthTotals = useMemo(() => {
        const t = {};
        months.forEach(m => {
            t[m] = { cur: 0, prev: 0 };
            filtered.forEach(r => {
                const d = r.months[m] || { cur: 0, prev: 0 };
                t[m].cur  += d.cur;
                t[m].prev += d.prev;
            });
        });
        return t;
    }, [filtered, months]);

    const grandTotals = useMemo(() => filtered.reduce((acc, r) => {
        Object.values(r.months).forEach(d => { acc.cur += d.cur; acc.prev += d.prev; });
        return acc;
    }, { cur: 0, prev: 0 }), [filtered]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'YearOnYearPurchaseReport', 0);
        if (!filtered.length) return;
        const subHeaders = months.flatMap(m => [`${MONTH_NAMES[(m - 1) % 12]} Cur`, `${MONTH_NAMES[(m - 1) % 12]} Prev`]);
        const headers = ['#', 'Item Name', ...subHeaders, 'Total Cur', 'Total Prev'];
        const rows = filtered.map((r, i) => {
            let tc = 0, tp = 0;
            const cells = months.flatMap(m => {
                const d = r.months[m] || { cur: 0, prev: 0 };
                tc += d.cur; tp += d.prev;
                return [d.cur, d.prev];
            });
            return [i + 1, r.ItemName, ...cells, tc, tp];
        });
        const totalRow = ['', 'Totals', ...months.flatMap(m => [monthTotals[m].cur, monthTotals[m].prev]), grandTotals.cur, grandTotals.prev];

        await exportStyledExcel({
            reportTitle: 'YEAR ON YEAR PURCHASE COMPARISON',
            sheetName: 'YoY Purchase',
            fileName: `YearOnYearPurchase_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: totalRow,
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap', border: `1px solid ${PRIMARY_MID}`, color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 10, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    const viewOptions = [
        { key: 'current',  label: 'Current Year' },
        { key: 'previous', label: 'Previous Year' },
        { key: 'both',     label: 'Both' },
    ];

    // cols per month: both=4 (cur,prev,diff,growth), single=1
    const colsPerMonth = view === 'both' ? 4 : 1;
    const totalCols = view === 'both' ? 4 : 1;
    const colSpanTotal = 2 + months.length * colsPerMonth + totalCols;

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading year on year purchase comparison..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Year On Year Purchase
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Purchase Analysis Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    {/* View toggle */}
                    <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                        {viewOptions.map(o => (
                            <button key={o.key} onClick={() => setView(o.key)}
                                style={{ height: 26, padding: '0 10px', fontSize: 10, fontWeight: 700, borderRadius: 6, cursor: 'pointer',
                                    border: `1px solid ${view === o.key ? '#fff' : 'rgba(255,255,255,0.3)'}`,
                                    background: view === o.key ? '#fff' : 'rgba(255,255,255,0.1)',
                                    color: view === o.key ? PRIMARY : '#fff', transition: 'all 0.15s' }}>
                                {o.label}
                            </button>
                        ))}
                    </div>
                    <input placeholder="Search item..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 150, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary cards */}
                {fetched && filtered.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Items',         value: filtered.length,               color: PRIMARY },
                            { label: 'Current Year',  value: `₹${fmtV(grandTotals.cur)}`,  color: PRIMARY },
                            { label: 'Previous Year', value: `₹${fmtV(grandTotals.prev)}`, color: '#0277bd' },
                            { label: 'Growth',
                              value: grandTotals.prev
                                ? `${((grandTotals.cur - grandTotals.prev) / grandTotals.prev * 100).toFixed(1)}%`
                                : '-',
                              color: grandTotals.cur >= grandTotals.prev ? '#059669' : '#dc2626' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {filtered.length} items
                        {search && ` (filtered from ${items.length})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 190px)', scrollbarWidth: 'thin', scrollbarColor: `${PRIMARY} #f0f0f0` }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 10, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 32 }} rowSpan={2}>#</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 200 }} rowSpan={2}>Item Name</th>
                                {months.map(m => (
                                    <th key={m} style={{ ...thBase, textAlign: 'center', minWidth: view === 'both' ? 300 : 90 }}
                                        colSpan={colsPerMonth}>
                                        {MONTH_NAMES[(m - 1) % 12]}
                                    </th>
                                ))}
                                <th style={{ ...thBase, textAlign: 'center', minWidth: view === 'both' ? 300 : 90, background: '#7f1d1d' }}
                                    colSpan={totalCols}>Total</th>
                            </tr>
                            {view === 'both' && (
                                <tr>
                                    {months.map(m => (
                                        <React.Fragment key={m}>
                                            <th style={{ ...thBase, textAlign: 'right', fontSize: 9, background: PRIMARY_MID }}>Cur</th>
                                            <th style={{ ...thBase, textAlign: 'right', fontSize: 9, background: '#1565c0' }}>Prev</th>
                                            <th style={{ ...thBase, textAlign: 'right', fontSize: 9, background: '#4a148c' }}>Diff</th>
                                            <th style={{ ...thBase, textAlign: 'right', fontSize: 9, background: '#1b5e20' }}>Growth%</th>
                                        </React.Fragment>
                                    ))}
                                    <th style={{ ...thBase, textAlign: 'right', fontSize: 9, background: '#7f1d1d' }}>Cur</th>
                                    <th style={{ ...thBase, textAlign: 'right', fontSize: 9, background: '#0d47a1' }}>Prev</th>
                                    <th style={{ ...thBase, textAlign: 'right', fontSize: 9, background: '#4a148c' }}>Diff</th>
                                    <th style={{ ...thBase, textAlign: 'right', fontSize: 9, background: '#1b5e20' }}>Growth%</th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={colSpanTotal} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => {
                                let totalCur = 0, totalPrev = 0, totalDiff = 0;
                                return (
                                    <tr key={r.ItemId} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT }}>
                                        <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                                        <td style={{ ...tdBase, fontWeight: 600, color: PRIMARY }}>{r.ItemName || '-'}</td>
                                        {months.map(m => {
                                            const d = r.months[m] || { cur: 0, prev: 0, diff: 0, growth: 0 };
                                            totalCur  += d.cur;
                                            totalPrev += d.prev;
                                            totalDiff += d.diff;
                                            if (view === 'current') return (
                                                <td key={m} style={{ ...tdBase, textAlign: 'right', color: d.cur ? PRIMARY : '#cbd5e1' }}>
                                                    {d.cur ? fmtV(d.cur) : '-'}
                                                </td>
                                            );
                                            if (view === 'previous') return (
                                                <td key={m} style={{ ...tdBase, textAlign: 'right', color: d.prev ? '#0277bd' : '#cbd5e1' }}>
                                                    {d.prev ? fmtV(d.prev) : '-'}
                                                </td>
                                            );
                                            return (
                                                <React.Fragment key={m}>
                                                    <td style={{ ...tdBase, textAlign: 'right', color: d.cur ? PRIMARY : '#cbd5e1' }}>
                                                        {d.cur ? fmtV(d.cur) : '-'}
                                                    </td>
                                                    <td style={{ ...tdBase, textAlign: 'right', color: d.prev ? '#0277bd' : '#cbd5e1' }}>
                                                        {d.prev ? fmtV(d.prev) : '-'}
                                                    </td>
                                                    <td style={{ ...tdBase, textAlign: 'right', color: d.diff > 0 ? '#4a148c' : d.diff < 0 ? '#dc2626' : '#cbd5e1' }}>
                                                        {d.diff ? fmtV(d.diff) : '-'}
                                                    </td>
                                                    <td style={{ ...tdBase, textAlign: 'right' }}>
                                                        {d.growth ? (
                                                            <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
                                                                background: d.growth >= 0 ? '#dcfce7' : '#fee2e2',
                                                                color: d.growth >= 0 ? '#059669' : '#dc2626' }}>
                                                                {d.growth >= 0 ? '▲' : '▼'} {Math.abs(d.growth).toFixed(1)}%
                                                            </span>
                                                        ) : '-'}
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}
                                        {view === 'current'  && <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtV(totalCur)}</td>}
                                        {view === 'previous' && <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(totalPrev)}</td>}
                                        {view === 'both' && <>
                                            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtV(totalCur)}</td>
                                            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(totalPrev)}</td>
                                            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#4a148c' }}>{fmtV(totalDiff)}</td>
                                            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700 }}>
                                                {totalPrev ? (
                                                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
                                                        background: totalCur >= totalPrev ? '#dcfce7' : '#fee2e2',
                                                        color: totalCur >= totalPrev ? '#059669' : '#dc2626' }}>
                                                        {totalCur >= totalPrev ? '▲' : '▼'} {Math.abs(((totalCur - totalPrev) / totalPrev) * 100).toFixed(1)}%
                                                    </span>
                                                ) : '-'}
                                            </td>
                                        </>}
                                    </tr>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                                    <td colSpan={2} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    {months.map(m => {
                                        const t = monthTotals[m] || { cur: 0, prev: 0 };
                                        const diff = t.cur - t.prev;
                                        const growth = t.prev ? ((t.cur - t.prev) / t.prev) * 100 : 0;
                                        if (view === 'current')  return <td key={m} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtV(t.cur)}</td>;
                                        if (view === 'previous') return <td key={m} style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(t.prev)}</td>;
                                        return (
                                            <React.Fragment key={m}>
                                                <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtV(t.cur)}</td>
                                                <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(t.prev)}</td>
                                                <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#4a148c' }}>{fmtV(diff)}</td>
                                                <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700 }}>
                                                    {t.prev ? (
                                                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
                                                            background: growth >= 0 ? '#dcfce7' : '#fee2e2',
                                                            color: growth >= 0 ? '#059669' : '#dc2626' }}>
                                                            {growth >= 0 ? '▲' : '▼'} {Math.abs(growth).toFixed(1)}%
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                            </React.Fragment>
                                        );
                                    })}
                                    {view === 'current'  && <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtV(grandTotals.cur)}</td>}
                                    {view === 'previous' && <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(grandTotals.prev)}</td>}
                                    {view === 'both' && <>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtV(grandTotals.cur)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(grandTotals.prev)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#4a148c' }}>{fmtV(grandTotals.cur - grandTotals.prev)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700 }}>
                                            {grandTotals.prev ? (
                                                <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
                                                    background: grandTotals.cur >= grandTotals.prev ? '#dcfce7' : '#fee2e2',
                                                    color: grandTotals.cur >= grandTotals.prev ? '#059669' : '#dc2626' }}>
                                                    {grandTotals.cur >= grandTotals.prev ? '▲' : '▼'} {Math.abs(((grandTotals.cur - grandTotals.prev) / grandTotals.prev) * 100).toFixed(1)}%
                                                </span>
                                            ) : '-'}
                                        </td>
                                    </>}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default YearOnYearPurchaseReport;
